import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Navbar, NavbarBrand, Nav, NavItem, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import SvgIcon from 'components/SvgIcon';
import Avatar from 'components/Avatar';
import CircularLogo from 'components/CircularLogo';
import { ROUTES } from 'app/routes';
import { deleteSession, selectIsJobSeekerAuthenticated, selectJobSeeker, getCurrentJobSeeker } from 'features/job-seeker-auth';
import { selectJobSeekerOnBehalfOfOrganizationAdminId } from 'features/job-seeker-on-behalf-of-organization-admin';
import { selectJobSeekerOnBehalfOfOrganizationId } from 'features/job-seeker-on-behalf-of-organization';
import OnBehalfOfOrganizationAdminSwitch from 'components/OnBehalfOfOrganizationAdminSwitch';
import ShowForUserOrMemberRoles from 'components/ShowForUserOrMemberRoles';
import { MemberRoles } from 'features/types';
import { checkIsAtLeastOneProgramLead } from 'utils/job-seeker-on-behalf-of-organization-helper';
import './Header.scss';

type Props = {
  toggleSidebar(): void;
};

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const JobSeekerHeader = ({ toggleSidebar }: Props) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsJobSeekerAuthenticated);
  const jobSeeker = useAppSelector(selectJobSeeker);
  const onBehalfOfOrganizationAdminId = useAppSelector(selectJobSeekerOnBehalfOfOrganizationAdminId);
  const onBehalfOfOrganizationId = useAppSelector(selectJobSeekerOnBehalfOfOrganizationId);

  const history = useHistory();

  const [dropdownIsOpen, setDropdownIsOpen] = useState<boolean>(false);

  const toggleDropdown = (): void => setDropdownIsOpen(!dropdownIsOpen);
  const logOut = (): void => dispatch(deleteSession());

  useEffect(() => {
    dispatch(getCurrentJobSeeker());
  }, [dispatch]);

  const currentUrl = window.location.pathname;
  const urlFragments = ['job-seeker/job-seekers', 'job-seeker/program-networks'];
  const urlMatch = urlFragments.some(fragment => currentUrl.includes(fragment));
  const verifiedOrganizationsWithLogo = (jobSeeker?.verifiedOrganizations || [])
    .filter(org => typeof org.logo === 'string' && org.logo.trim() !== '' && isValidUrl(org.logo))
    .filter(org => {
      if (onBehalfOfOrganizationAdminId) return org.id === onBehalfOfOrganizationAdminId;
      if (urlMatch && onBehalfOfOrganizationId) return org.id === onBehalfOfOrganizationId;
      return true;
    })
    .map(org => ({
      id: org.id,
      name: org.name,
      logo: org.logo as string
    }));

  const isAtLeastOneProgramLead = checkIsAtLeastOneProgramLead(jobSeeker);

  return (
    <>
      <Navbar className="navbar-header jobseeker-navbar-header px-0" dark expand="md">
        <button className="navbar-toggler" onClick={toggleSidebar}>
          <SvgIcon name="sidebar/toggle-sidebar" />
        </button>
        {verifiedOrganizationsWithLogo.length > 0 ?
          <div className="community-custom-logos-container">
            {verifiedOrganizationsWithLogo.map((org, index) => {
              return (
                <NavbarBrand to={ROUTES.JOB_SEEKER.RESOURCES} tag={Link}>
                  <CircularLogo logo={org.logo} name={org.name} size={48} wrap={false} />
                </NavbarBrand>
              );
            })}
          </div>
          :
          <NavbarBrand to={ROUTES.JOB_SEEKER.RESOURCES} tag={Link}>
            <SvgIcon name="logo" className="logo-with-text" title="WorkerSpring" />
            <SvgIcon name="logo-no-text" className="logo-no-text" title="WorkerSpring" />
          </NavbarBrand>
        }
        <Nav className="ms-auto align-items-center" navbar>
          {(isAuthenticated && jobSeeker) && <>
            <ShowForUserOrMemberRoles roles={[MemberRoles.FullAdmin, MemberRoles.ViewOnlyAdmin]}>
              <NavItem className="d-sm-block">
                <OnBehalfOfOrganizationAdminSwitch />
              </NavItem>
            </ShowForUserOrMemberRoles>
            <NavItem>
              <Dropdown isOpen={dropdownIsOpen} toggle={toggleDropdown}>
                <DropdownToggle>
                  <div className="user-profile">
                    <Avatar src={jobSeeker.photo} size={40} />
                    <div className="btn-user-profile">
                      <div>
                        <div className="navbar-username" title={jobSeeker.name}>{jobSeeker.name}</div>
                      </div>
                      <SvgIcon name="chevron" className="dropdown-arrow" />
                    </div>
                  </div>
                </DropdownToggle>
                <DropdownMenu className="job-seeker" end={true}>
                  <DropdownItem className="menu-item" onClick={() => history.push(ROUTES.JOB_SEEKER.PROFILE_PAGE)}>
                    My profile
                  </DropdownItem>
                  {isAtLeastOneProgramLead &&
                    <DropdownItem className="menu-item" onClick={() => history.push(ROUTES.JOB_SEEKER.MEMBER_APPLICATIONS_FOR_LEAD)}>
                      Member Applications
                    </DropdownItem>
                  }
                  <DropdownItem className="menu-item signout-item" onClick={logOut}>
                    Sign out <SvgIcon name="signout" />
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </NavItem>
          </>}
        </Nav>
      </Navbar>
    </>
  );
};

export default JobSeekerHeader;
